import axios from 'axios';
import { captureException } from '@sentry/react';
import {
  createContext,
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { be } from 'models/conent';
import ConsentScripts from './ConsentScripts';
import CookieBanner from './CookieBanner';
import CookiePreferences from './CookiePreferences';
import {
  clearLegacyCookies,
  Consent,
  ConsentChoice,
  CONSENT_VERSION,
  readConsent,
  toGtagConsent,
  writeConsent,
} from './consent';

interface CookieConsentState {
  consent: Consent | null;
  choose: (choice: ConsentChoice) => void;
}

const CookieConsentContext = createContext<CookieConsentState>({
  consent: null,
  choose: () => {},
});

declare global {
  interface Window {
    dataLayer?: unknown[];
  }
}

// gtag() is only a push onto dataLayer; pushing `arguments` directly keeps the
// update working even if GTM itself has not loaded yet.
function pushGtagConsent(choice: ConsentChoice) {
  window.dataLayer = window.dataLayer || [];
  // eslint-disable-next-line prefer-rest-params
  (function gtag(..._args: unknown[]) {
    window.dataLayer.push(arguments);
  })('consent', 'update', toGtagConsent(choice));
}

// The server log is proof of consent, not the source of truth: a failure here
// (network, rate limit) must never block the visitor or re-show the banner.
const logConsent = (consent: Consent) => {
  axios
    .post(`${be}/api/cookie-consents`, {
      data: {
        consent_id: consent.id,
        analytics: consent.analytics,
        marketing: consent.marketing,
        version: CONSENT_VERSION,
      },
    })
    .catch((error) => captureException(error));
};

const ALL: ConsentChoice = { analytics: true, marketing: true };

export const CookieConsentProvider: FC<any> = ({ children }) => {
  const [ready, setReady] = useState(false);
  const [consent, setConsent] = useState<Consent | null>(null);
  const [view, setView] = useState<'banner' | 'preferences'>('banner');

  useEffect(() => {
    clearLegacyCookies();
    setConsent(readConsent());
    setReady(true);
  }, []);

  const choose = useCallback(
    (choice: ConsentChoice) => {
      const saved = writeConsent(choice, consent);
      pushGtagConsent(saved);
      setConsent(saved);
      logConsent(saved);
    },
    [consent],
  );

  const value = useMemo(() => ({ consent, choose }), [consent, choose]);

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
      <ConsentScripts consent={consent} />
      {ready && !consent && view === 'banner' ? (
        <CookieBanner
          onChangePreference={() => setView('preferences')}
          onAcceptAll={() => choose(ALL)}
        />
      ) : null}
      {ready && !consent && view === 'preferences' ? (
        <CookiePreferences
          initial={{ analytics: false, marketing: false }}
          onClose={() => setView('banner')}
          onAcceptAll={() => choose(ALL)}
          onAcceptSelected={choose}
        />
      ) : null}
    </CookieConsentContext.Provider>
  );
};

export const useCookieConsent = () => useContext(CookieConsentContext);
